import { isAllowedMailWebLink } from './emailContentSanitizer';

export const getProviderDisplayName = (provider) => (provider === 'microsoft' ? 'Outlook' : 'Gmail');

export const MAIL_MUTATION_ACTION_TYPES = ['archive', 'move-to-label', 'reply', 'forward', 'draft', 'send'];

export const isMailMutationAction = (action) => MAIL_MUTATION_ACTION_TYPES.includes(action?.type);

const MAIL_ACTION_OPERATIONS = Object.freeze({
  archive: 'archive_email',
  'move-to-label': 'move_email',
  reply: 'create_reply_draft',
  forward: 'create_forward_draft',
  draft: 'create_draft',
  send: 'send_draft',
  markRead: 'mark_email_as_read',
});

export const getMailActionOperation = (action) => MAIL_ACTION_OPERATIONS[action?.type] ?? null;

export const resolveMailProvider = ({ mailbox, email } = {}) =>
  mailbox?.provider ?? email?.provider ?? null;

const MAIL_WRITE_CAPABILITIES = Object.freeze({
  google: new Set([
    'archive_email', 'move_email', 'mark_email_as_read', 'restore_email',
    'create_draft', 'create_reply_draft', 'create_forward_draft',
    'send_email', 'send_draft', 'queue_user_email_action',
  ]),
  microsoft: new Set([
    'archive_email',
    'move_email',
    'restore_email',
    'mark_email_as_read',
    'create_draft',
    'create_reply_draft',
    'create_forward_draft',
    'queue_user_email_action',
  ]),
});

const MAIL_WRITE_OPERATION_ALIASES = Object.freeze({
  reply_to_email: 'create_reply_draft',
  forward_email: 'create_forward_draft',
});

const normalizeMailWriteOperation = (operation) => (
  MAIL_WRITE_OPERATION_ALIASES[operation] ?? operation
);

const hasHydratedMicrosoftMailbox = (context) => {
  const mailbox = context?.mailbox;
  return typeof (mailbox?.id ?? mailbox?.mailIntegrationId) === 'string'
    && (mailbox.id ?? mailbox.mailIntegrationId).length > 0;
};

export const canWriteMail = (context, operation = 'archive_email') => {
  const provider = resolveMailProvider(context);
  const capabilities = MAIL_WRITE_CAPABILITIES[provider];
  const normalizedOperation = normalizeMailWriteOperation(operation);

  if (provider === 'google') return capabilities.has(normalizedOperation);
  if (provider !== 'microsoft' || !hasHydratedMicrosoftMailbox(context)) return false;

  return capabilities?.has(normalizedOperation) ?? false;
};

export const mailWritesBlocked = (context, operation) => !canWriteMail(context, operation);

export const canDisplayMailAction = (context, action) => {
  const operation = getMailActionOperation(action);
  return !operation || canWriteMail(context, operation);
};

export const MAIL_WRITE_BLOCKED_EVENT = 'prince-geoffrey:mail-write-blocked';

export const getMailWriteBlockedMessage = (context) => (
  resolveMailProvider(context) === 'microsoft'
    ? 'Mail actions are not available for Outlook in this release.'
    : 'Mailbox context is unavailable. Sync your inbox before trying that mail action again.'
);

const notifyMailWriteBlocked = (operation, context) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;

  window.dispatchEvent(new CustomEvent(MAIL_WRITE_BLOCKED_EVENT, {
    detail: {
      operation,
      message: getMailWriteBlockedMessage(context),
      provider: resolveMailProvider(context),
    },
  }));
};

export const getMailWriteContext = ({ currentDigest, mailbox, email, emailId } = {}) => ({
  mailbox: mailbox ?? currentDigest?.mailbox,
  email: email ?? currentDigest?.content?.find((item) => item?.id === emailId),
});

export const refuseMailWrite = (operation, context) => {
  if (canWriteMail(context, operation)) return null;

  const provider = resolveMailProvider(context);
  const userMessage = getMailWriteBlockedMessage(context);

  console.warn(`Blocked ${operation}: this mailbox provider does not support mail writes`, {
    provider,
  });
  notifyMailWriteBlocked(operation, context);

  return {
    blocked: true,
    provider,
    userMessage,
    executedAction: null,
    data: null,
  };
};

export const getMailboxSyncPresentation = (mailbox) => {
  const provider = mailbox?.provider === 'microsoft' ? 'microsoft' : 'google';
  const providerName = getProviderDisplayName(provider);
  return {
    provider,
    iconSrc: provider === 'microsoft' ? '/outlook_icon.svg' : '/gmail_icon_2020.svg',
    iconAlt: providerName,
    trackingDescription: `Sync ${providerName} inbox`,
  };
};

export const resolveOriginalMessageLink = (link) => {
  const providerName = getProviderDisplayName(link?.provider);
  const isAvailable = isAllowedMailWebLink(link?.webLink);

  return {
    isAvailable,
    href: isAvailable ? link.webLink : null,
    providerName,
    label: `Open in ${providerName}`,
    unavailableText: 'Original message link is unavailable',
  };
};
